/**
 * Maps the Report API's 400 ValidationProblemDetails `errors` dict back to the evaluation-state
 * paths used on the About page, so a validation failure not already caught client-side (see
 * reportPayloadSanitization.js) can still be handled gracefully instead of blocking autosave.
 *
 * The `errors` keys come in three shapes:
 *   - bare field name: "productName", "phone"
 *   - dotted, mixed-case container: "author.WebsiteUrl", "vendorContact.Phone"
 *   - JSON path (deserialization failures): "$.completionPercentage"
 *
 * Only keys we can (a) safely blank in a retry payload and (b) show against a real About page
 * field are mapped; everything else is left unmapped so we never silently discard content we
 * can't represent back to the user (e.g. Notes, License, ProductName).
 *
 * A bare `phone` key is ambiguous between Author and VendorContact, so it maps to both evaluation
 * paths rather than guessing which one failed.
 */
const NORMALIZED_KEY_TO_EVALUATION_PATHS = {
  "author.email": ["author.email"],
  "author.phone": ["author.phone"],
  "author.websiteurl": ["author.website"],
  "vendorcontact.email": ["vendor.email"],
  "vendorcontact.phone": ["vendor.phone"],
  "vendorcontact.websiteurl": ["vendor.website"],
  "reportdetails.repositoryurl": ["repository"],
  "reportdetails.feedbackurl": ["feedback"],
  repositoryurl: ["repository"],
  feedbackurl: ["feedback"],
  phone: ["author.phone", "vendor.phone"],
};

const EVALUATION_PATH_TO_PAYLOAD_LOCATION = {
  "author.email": { containerKey: "author", payloadKey: "email" },
  "author.phone": { containerKey: "author", payloadKey: "phone" },
  "author.website": { containerKey: "author", payloadKey: "websiteUrl" },
  "vendor.email": { containerKey: "vendorContact", payloadKey: "email" },
  "vendor.phone": { containerKey: "vendorContact", payloadKey: "phone" },
  "vendor.website": { containerKey: "vendorContact", payloadKey: "websiteUrl" },
  repository: { containerKey: "reportDetails", payloadKey: "repositoryUrl" },
  feedback: { containerKey: "reportDetails", payloadKey: "feedbackUrl" },
};

function normalizeKey(key) {
  return String(key ?? "").replace(/^\$\./, "").toLowerCase();
}

/**
 * Pulls the `errors` dict out of an axios response (or an axios error's `.response`) if it looks
 * like a Report API validation failure (400 with a plain-object `errors` field). Returns null for
 * any other shape (network error, 401/403/404/500, or a 400 without an `errors` dict) so callers
 * can fall back to today's generic failure handling for those cases.
 */
export function getValidationErrorsFromResponse(errorOrResponse) {
  const response = errorOrResponse?.response ?? errorOrResponse;
  if (!response || response.status !== 400) return null;
  const errors = response?.data?.errors;
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) return null;
  return errors;
}

/**
 * @param {Record<string, string[]|string>} errors - the `errors` dict from a ValidationProblemDetails response.
 * @returns {{
 *   fieldErrors: Record<string,string>,
 *   matchedEvaluationPaths: string[],
 *   hasUnmatchedKeys: boolean,
 * }}
 */
export function mapReportApiValidationErrors(errors) {
  const fieldErrors = {};
  const matchedEvaluationPaths = new Set();
  let hasUnmatchedKeys = false;

  Object.entries(errors ?? {}).forEach(([key, messages]) => {
    const paths = NORMALIZED_KEY_TO_EVALUATION_PATHS[normalizeKey(key)];
    if (!paths) {
      hasUnmatchedKeys = true;
      return;
    }
    const messageList = Array.isArray(messages) ? messages : [messages];
    const message = messageList.find((m) => typeof m === "string" && m.trim()) || "";
    paths.forEach((path) => {
      matchedEvaluationPaths.add(path);
      if (message) fieldErrors[path] = message;
    });
  });

  return {
    fieldErrors,
    matchedEvaluationPaths: Array.from(matchedEvaluationPaths),
    hasUnmatchedKeys,
  };
}

/**
 * Returns a copy of `payload` with the fields identified by `evaluationPaths` (as returned by
 * mapReportApiValidationErrors) blanked, so a retried save can proceed without the values the API
 * rejected. Does not mutate `payload`.
 */
export function blankPayloadFields(payload, evaluationPaths) {
  if (!payload) return payload;

  const containerUpdates = {};
  (evaluationPaths ?? []).forEach((path) => {
    const location = EVALUATION_PATH_TO_PAYLOAD_LOCATION[path];
    if (!location || !payload[location.containerKey]) return;
    containerUpdates[location.containerKey] = {
      ...(containerUpdates[location.containerKey] ?? payload[location.containerKey]),
      [location.payloadKey]: "",
    };
  });

  if (Object.keys(containerUpdates).length === 0) return payload;
  return { ...payload, ...containerUpdates };
}
